import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtom } from 'jotai';
import useAPI from '../../api';
import SectionPage from '../../Components/Paper/SectionPage';
import BranchesListItems from './Components/BranchesListItems';
import BranchDialog from './Components/BranchDialog';
import { openSnackbarAdviseAtom, searchValuesAtom } from '../../Atoms';
import BranchesFilters from './Components/BranchesFilters';
import BRANCH_SORTING from './Utils/constants';
import sortByProperty from '../../Helpers/Arrays/sortByProperty';
import BranchSourceDialog from './Components/BranchSourceDialog';

const getBranchListItem = (
  data,
  onClick,
  userInfo,
  toggleAccordion,
  expanded,
  branchSources,
  openEditBranchSourceDialog
) => (
  <BranchesListItems
    key={data.id}
    data={data}
    onClick={onClick}
    userInfo={userInfo}
    toggleAccordion={toggleAccordion}
    expanded={expanded}
    branchSources={branchSources}
    openEditBranchSourceDialog={openEditBranchSourceDialog}
  />
);

const BranchManagementPage = function BranchManagementPage(userInfo) {
  const intl = useIntl();
  const API = useAPI();

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const [branchDialog, setBranchDialog] = useState(null);
  const [branchSourceDialog, setBranchSourceDialog] = useState(null);
  const [branch, setBranch] = useState([]);
  const [branchSources, setBranchSources] = useState([]);
  const [dialogId, setDialogId] = useState('');
  const [title, setTitle] = useState('');
  const [expanded, setExpanded] = useState(false);

  const toggleAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const openEditBranchDialog = (branchData) => {
    setDialogId('editBranch');
    setTitle(`${intl.messages.EDIT_BRANCH} ${branchData.branchDescription}`);
    setBranchDialog(branchData);
  };
  const handleOpenNewBranchDialog = () => {
    setDialogId('newBranch');
    setTitle(intl.messages.NEW_BRANCH);
    setBranchDialog(true);
  };

  const openEditBranchSourceDialog = (branchSourceData) => {
    setBranchSourceDialog(branchSourceData);
  };

  useEffect(() => {
    API.getBranches(
      (res) => {
        setBranch(res);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_LOADING_BRANCHES,
          severity: 'error',
        });
      }
    );
    API.getBranchSources(
      (res) => {
        setBranchSources(res);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_LOADING_BRANCHSOURCES,
          severity: 'error',
        });
      }
    );
  }, []);

  /****/
  const handleSubmit = (data, onSuccess) => {
    if (!data.id) {
      API.createBranch(
        data,
        (newBranch) => {
          setBranch((prev) => [newBranch, ...prev]);
          setBranchDialog(null);
          setDialogId('');
          onSuccess();
          setOpenSnackbarAdvise({
            message: intl.messages.NEW_BRANCH_CREATE_SUCCESSFULLY,
            severity: 'success',
          });
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.CREATE_BRANCH_ERROR}`,
            severity: 'error',
          });
        }
      );
    } else {
      API.updateBranch(
        data,
        (editBranch) => {
          setBranch((prev) =>
            prev.map((item) => (item.id === editBranch.id ? editBranch : item))
          );
          setBranchDialog(null);
          setDialogId('');
          setOpenSnackbarAdvise({
            message: intl.messages.BRANCH_EDITED_SUCCESSFULLY,
            severity: 'success',
          });
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.EDITING_BRANCH_ERROR}`,
            severity: 'error',
          });
        }
      );
    }
  };

  const handleSubmitBranchSource = (data) => {
    API.updateBranchSource(
      data,
      (editBranchSource) => {
        setBranchSources((prev) =>
          prev.map((item) =>
            item.branchSourceId === editBranchSource.branchSourceId
              ? editBranchSource
              : item
          )
        );
        setBranchSourceDialog(null);
        setOpenSnackbarAdvise({
          message: intl.messages.BRANCHSOURCE_EDITED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.EDITING_BRANCHSOURCE_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const filteredAndSortedBranches = useMemo(() => {
    if (!branch?.length) return [];

    let filteredBranches = [...branch];
    if (searchValues?.searchBox?.trim()) {
      const search = searchValues.searchBox.toUpperCase();
      filteredBranches = filteredBranches.filter(({ branchDescription }) =>
        branchDescription?.toUpperCase().includes(search)
      );
    }

    if (searchValues?.deleted !== undefined && searchValues?.deleted !== null) {
      filteredBranches = filteredBranches.filter(
        ({ deleted }) => deleted === searchValues.deleted
      );
    }

    return sortByProperty(
      filteredBranches,
      searchValues?.sortValue?.value,
      searchValues?.sortValue?.direction === 'asc' ? 'ASCENDING' : 'DESCENDING'
    );
  }, [searchValues, branch]);

  const { userRoles } = userInfo;
  const isSuperAdmin = userRoles.find((role) => role.roleId === 'SUPERADMIN');
  return (
    <>
      <SectionPage
        hideExportExcel
        hideFilters
        fab={isSuperAdmin ? intl.messages.NEW_BRANCH : null}
        onClickFab={isSuperAdmin ? handleOpenNewBranchDialog : null}
        fabVariant="extended"
        sectionName="BRANCHES_MANAGEMENT"
        sectionSubName="BRANCHES"
        customEntryFunction={(item) =>
          getBranchListItem(
            item,
            openEditBranchDialog,
            userInfo,
            toggleAccordion,
            expanded,
            branchSources,
            openEditBranchSourceDialog
          )
        }
        singlePageList={filteredAndSortedBranches}
        customFilters={
          <BranchesFilters
            defaultValues={{
              searchBox: searchValues?.searchBox || '',
            }}
            onChangeFilters={(newSearchValues) => {
              setSearchValues((prev) => ({
                ...prev,
                ...newSearchValues,
              }));
            }}
          />
        }
        sortingOptions={BRANCH_SORTING}
      />
      <BranchDialog
        dialogId={dialogId}
        title={title}
        onSubmit={handleSubmit}
        open={Boolean(branchDialog)}
        branchData={branchDialog}
        handleClose={() => {
          setBranchDialog(null);
          setTitle(null);
        }}
      />
      <BranchSourceDialog
        onSubmit={handleSubmitBranchSource}
        open={Boolean(branchSourceDialog)}
        branchSourceData={branchSourceDialog}
        handleClose={() => {
          setBranchSourceDialog(null);
        }}
      />
    </>
  );
};
export default BranchManagementPage;
